/* ============================================================
   Capital-Stack Builder

   Four layers in, one question out: does the operation cover its debt
   service in the year that actually hurts?

   The tool is built around a single observation. Sponsors quote their
   year-one coverage because that is the number the model spits out
   first, and year one is very often the best year the venture will ever
   have — because the DFI tranche is still in grace and nothing is
   amortising yet. The year the grace period ends is the year the file
   is really tested, and it is the number a credit committee will find
   whether or not the sponsor brought it.

   So the headline is the WORST serviced year, named and dated, with the
   year-one figure shown beside it rather than instead of it.
   ============================================================ */

import { LAYERS, buildStack, readStack, dscrBand } from '../../data/capital.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { money, number, percent, parseNumber } from '../../shared/format.js';
import { saveRecord } from '../../shared/records.js';
import { readable } from '../../shared/tone.js';
import { PrintButton } from '../../components/PrintButton.js';

export function CapitalPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Capital-Stack Builder</h1>
      <p>
        Debt, equity and grant layers assembled into one stack — with coverage read across the
        whole life of the loans, not just the first year.
      </p>
    </header>

    <div id="cap-form">
      <section class="section">
        <h2 class="section-title">What the money is for</h2>
        <div class="card">
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Total funding requirement (USD)</span>
              <input id="cap-uses" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="10000" placeholder="1000000" />
              <span class="field-hint">Assets, working capital and fees — everything the raise has to cover.</span>
            </label>
            <label class="field">
              <span class="field-label">Annual EBITDA once operating (USD)</span>
              <input id="cap-ebitda" class="input-field num" type="number" inputmode="decimal"
                     step="10000" placeholder="180000" />
              <span class="field-hint">The cash the operation throws off before debt service.</span>
            </label>
          </div>
          <label class="field" style="margin-bottom:0;">
            <span class="field-label">Your target return on equity (%)</span>
            <input id="cap-equity-return" class="input-field num" type="number" inputmode="decimal"
                   min="0" step="0.5" placeholder="20" />
            <span class="field-hint">
              Used only to cost the equity layer in the blended figure. Equity is not free, and a
              blended cost that pretends otherwise is not worth quoting.
            </span>
          </label>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">The layers</h2>
        <p class="section-caption">
          Leave any layer at zero if it is not part of the structure. Grace is the period during
          which only interest is paid.
        </p>
        ${LAYERS.map(
          (l) => html`
            <div class="card" style="margin-bottom: var(--ca-space-3);">
              <div class="row" style="align-items: baseline; gap: var(--ca-space-2); margin-bottom: var(--ca-space-3);">
                <span style="width:9px; height:9px; border-radius:2px; background:${l.accent}; flex-shrink:0;"></span>
                <h3 style="font-size: var(--ca-text-sm); margin:0;">${l.label}</h3>
              </div>
              <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin: 0 0 var(--ca-space-3);">
                ${l.blurb}
              </p>
              <div class="${l.kind === 'debt' ? 'grid-2' : ''}">
                <label class="field" style="${l.kind === 'debt' ? '' : 'margin-bottom:0;'}">
                  <span class="field-label">Amount (USD)</span>
                  <input id="cap-${l.id}-amount" class="input-field num" type="number"
                         inputmode="decimal" min="0" step="10000" placeholder="0" />
                </label>
                ${l.kind === 'debt'
                  ? html`
                      <label class="field">
                        <span class="field-label">Interest rate (% a year)</span>
                        <input id="cap-${l.id}-rate" class="input-field num" type="number"
                               inputmode="decimal" min="0" step="0.25" placeholder="0" />
                      </label>
                      <label class="field" style="margin-bottom:0;">
                        <span class="field-label">Tenor (years)</span>
                        <input id="cap-${l.id}-tenor" class="input-field num" type="number"
                               inputmode="numeric" min="0" max="40" step="1" placeholder="0" />
                      </label>
                      <label class="field" style="margin-bottom:0;">
                        <span class="field-label">Grace period (years)</span>
                        <input id="cap-${l.id}-grace" class="input-field num" type="number"
                               inputmode="numeric" min="0" max="20" step="1" placeholder="0" />
                        <span class="field-hint">Interest-only years before principal falls due.</span>
                      </label>
                    `
                  : ''}
              </div>
            </div>
          `
        )}
      </section>

      <div id="cap-error"></div>

      <button id="cap-calculate" class="btn btn-primary btn-block">
        Assemble the stack
      </button>
    </div>

    <div id="capital-results" hidden></div>
  `;

  page.querySelector('#cap-calculate').addEventListener('click', () => calculate(page));
  return page;
}

function readLayers(page) {
  const val = (id) => parseNumber(page.querySelector(`#${id}`).value);
  return LAYERS.map((l) => ({
    id: l.id,
    label: l.label,
    kind: l.kind,
    accent: l.accent,
    amount: val(`cap-${l.id}-amount`) ?? 0,
    rate: l.kind === 'debt' ? val(`cap-${l.id}-rate`) ?? 0 : 0,
    tenor: l.kind === 'debt' ? val(`cap-${l.id}-tenor`) ?? 0 : 0,
    grace: l.kind === 'debt' ? val(`cap-${l.id}-grace`) ?? 0 : 0
  }));
}

function calculate(page) {
  const val = (id) => parseNumber(page.querySelector(`#${id}`).value);
  const uses = val('cap-uses');
  const ebitda = val('cap-ebitda');
  const equityReturn = val('cap-equity-return') ?? 0;
  const layers = readLayers(page);

  const errors = [];
  if (uses === null || uses <= 0) errors.push('Enter a total funding requirement greater than zero.');
  if (ebitda === null) errors.push('Enter annual EBITDA — a negative figure is fine and informative.');
  if (layers.every((l) => (l.amount || 0) <= 0)) errors.push('Enter an amount on at least one layer.');
  for (const l of layers) {
    if (l.kind === 'debt' && l.amount > 0 && !(l.tenor > 0))
      errors.push(`${l.label}: enter a tenor, or the loan has no repayment period.`);
    if (l.kind === 'debt' && l.grace > l.tenor && l.tenor > 0)
      errors.push(`${l.label}: grace cannot exceed the tenor.`);
  }

  const errorSlot = page.querySelector('#cap-error');
  if (errors.length > 0) {
    errorSlot.innerHTML = html`
      <div class="notice notice-danger" style="margin-bottom: var(--ca-space-4);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">Cannot assemble yet</strong>
          <ul style="margin: var(--ca-space-1) 0 0 var(--ca-space-4); font-size: var(--ca-text-xs);">
            ${errors.map((e) => html`<li>${e}</li>`)}
          </ul>
        </div>
      </div>
    `;
    errorSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }
  errorSlot.innerHTML = '';

  /* Walk far enough to see every loan out. A horizon shorter than the
     longest tenor would hide the very year the tool exists to find. */
  const horizon = Math.max(10, ...layers.map((l) => l.tenor || 0));
  const result = buildStack({ uses, ebitda, equityReturn, layers, horizon });
  renderResults(page, layers, result);

  void saveRecord('capital', {
    sources: result.sources,
    gearing: result.gearing,
    blendedCost: result.cost,
    worstDscr: result.worst && Number.isFinite(result.worst.dscr) ? result.worst.dscr : null,
    worstYear: result.worst ? result.worst.year : null
  });
}

function renderResults(page, layers, r) {
  page.querySelector('#cap-form').hidden = true;

  const results = page.querySelector('#capital-results');
  results.hidden = false;
  results.className = 'animate-fade-in';
  results.replaceChildren();

  const funded = layers.filter((l) => l.amount > 0);

  /* ---- Headline: the worst year, not the first ---- */
  const head = document.createElement('div');
  head.style.cssText = 'text-align:center; margin-bottom: var(--ca-space-6);';
  head.innerHTML = html`
    <div class="badge" style="background: color-mix(in srgb, ${r.worstBand.tone} 13%, transparent); color:${readable(r.worstBand.tone)};">
      ${r.worstBand.label.toUpperCase()}
    </div>
    <div class="stat-figure num" style="margin-top: var(--ca-space-3); color:${readable(r.worstBand.tone)};">
      ${r.worst && Number.isFinite(r.worst.dscr) ? number(r.worst.dscr, 2) : '—'}
    </div>
    <div style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-top: var(--ca-space-1);">
      ${r.worst ? `debt service coverage in year ${r.worst.year}, the tightest year of the stack` : 'no debt to service'}
    </div>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-top: var(--ca-space-3); max-width: 46ch; margin-inline: auto;">
      ${r.worstBand.note}
    </p>
  `;
  results.appendChild(head);

  /* ---- Sources and uses ---- */
  const su = document.createElement('div');
  su.className = 'card';
  su.style.marginBottom = 'var(--ca-space-4)';
  su.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-4);">Sources against uses</h3>
    <div class="stack" style="gap: var(--ca-space-3);">
      ${funded.map(
        (l) => html`
          <div>
            <div class="row" style="align-items: baseline; justify-content: space-between;">
              <span class="row" style="align-items: baseline; gap: var(--ca-space-2);">
                <span style="width:9px;height:9px;border-radius:2px;background:${l.accent};flex-shrink:0;"></span>
                <span style="font-size: var(--ca-text-sm);">${l.label}</span>
              </span>
              <span class="num" style="font-size: var(--ca-text-sm);">${money(l.amount)}</span>
            </div>
            <div style="height:5px; border-radius:3px; background: var(--ca-surface-sunk); overflow:hidden; margin-top:5px;">
              <div style="height:100%; width:${r.sources > 0 ? (l.amount / r.sources) * 100 : 0}%; background:${l.accent};"></div>
            </div>
            <div style="font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary); margin-top:3px;">
              ${percent(r.sources > 0 ? (l.amount / r.sources) * 100 : 0, 1)} of the stack${l.kind === 'debt' && l.amount > 0
                ? ` · ${number(l.rate, 2)}% over ${number(l.tenor)} years${l.grace > 0 ? `, ${number(l.grace)} in grace` : ''}`
                : ''}
            </div>
          </div>
        `
      )}
    </div>
    <div class="row" style="justify-content: space-between; align-items: baseline; margin-top: var(--ca-space-4); padding-top: var(--ca-space-3); border-top: 1px solid var(--ca-border);">
      <span style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">Total sources</span>
      <span class="num" style="font-size: var(--ca-text-base);">${money(r.sources)}</span>
    </div>
    <div class="row" style="justify-content: space-between; align-items: baseline;">
      <span style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">Total uses</span>
      <span class="num" style="font-size: var(--ca-text-base);">${money(r.uses)}</span>
    </div>
    <div class="row" style="justify-content: space-between; align-items: baseline; margin-top: var(--ca-space-2);">
      <span style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
        ${r.gap > 0 ? 'Still to raise' : r.gap < 0 ? 'Raised above requirement' : 'Fully funded'}
      </span>
      <span class="num" style="font-size: var(--ca-text-base); color:${readable(
        Math.abs(r.gap) < 0.5 ? 'var(--ca-positive)' : r.gap > 0 ? 'var(--ca-danger)' : 'var(--ca-chart-2)'
      )};">
        ${Math.abs(r.gap) < 0.5 ? '—' : money(Math.abs(r.gap))}
      </span>
    </div>
  `;
  results.appendChild(su);

  /* ---- The shape of the stack ---- */
  const shape = document.createElement('div');
  shape.className = 'card';
  shape.style.marginBottom = 'var(--ca-space-4)';
  shape.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-4);">The shape of it</h3>
    <div class="grid-2" style="gap: var(--ca-space-4);">
      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl);">${percent(r.gearing, 1)}</div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          of total capital is debt
        </div>
      </div>
      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl);">${percent(r.cost, 2)}</div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          blended cost of capital, grant layer included at zero
        </div>
      </div>
      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl);">${money(r.peakService)}</div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          peak annual debt service
        </div>
      </div>
      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl);">${money(r.totalInterest)}</div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          total interest across the life of the stack
        </div>
      </div>
    </div>
  `;
  results.appendChild(shape);

  /* ---- Coverage over time ---- */
  if (r.schedule.some((y) => y.service > 0)) {
    const maxService = Math.max(...r.schedule.map((y) => y.service));
    const sched = document.createElement('div');
    sched.className = 'card';
    sched.style.marginBottom = 'var(--ca-space-4)';
    sched.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">Coverage, year by year</h3>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin: 0 0 var(--ca-space-4);">
        EBITDA is held flat, so this isolates the effect of the repayment profile rather than
        mixing it with a growth assumption.
      </p>
      <div class="stack" style="gap: var(--ca-space-2);">
        ${r.schedule
          .filter((y) => y.service > 0)
          .map((y) => {
            const band = dscrBand(y.dscr);
            const isWorst = r.worst && y.year === r.worst.year;
            return html`
              <div class="row" style="align-items: center; gap: var(--ca-space-3);">
                <span class="num" style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); width: 3.5ch; flex-shrink:0;">
                  ${y.year}
                </span>
                <span style="flex:1; height:16px; border-radius:3px; background: var(--ca-surface-sunk); overflow:hidden; position:relative;">
                  <span style="display:block; height:100%; width:${(y.service / maxService) * 100}%; background:${band.tone}; opacity:${isWorst ? 1 : 0.55};"></span>
                </span>
                <span class="num" style="font-size: var(--ca-text-xs); width: 9ch; text-align:right; flex-shrink:0;">
                  ${money(y.service)}
                </span>
                <span class="num" style="font-size: var(--ca-text-xs); width: 5ch; text-align:right; flex-shrink:0; color:${readable(band.tone)}; font-weight:${isWorst ? 'var(--ca-weight-medium)' : 'inherit'};">
                  ${Number.isFinite(y.dscr) ? number(y.dscr, 2) : '—'}
                </span>
              </div>
            `;
          })}
      </div>
      <div class="row" style="justify-content: flex-end; gap: var(--ca-space-3); margin-top: var(--ca-space-3);">
        <span style="font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary);">bar length = annual debt service · right column = DSCR</span>
      </div>
    `;
    results.appendChild(sched);
  }

  /* ---- What the numbers say ---- */
  const notes = readStack(r);
  if (notes.length > 0) {
    const read = document.createElement('div');
    read.className = 'card';
    read.style.marginBottom = 'var(--ca-space-4)';
    read.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-4);">What this stack says</h3>
      <div class="stack" style="gap: var(--ca-space-4);">
        ${notes.map(
          (n) => html`
            <div>
              <div class="badge" style="background: color-mix(in srgb, ${n.tone} 13%, transparent); color:${readable(n.tone)};">
                ${n.label}
              </div>
              <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin: var(--ca-space-2) 0 0;">
                ${n.text}
              </p>
            </div>
          `
        )}
      </div>
    `;
    results.appendChild(read);
  }

  /* ---- The limits of this ---- */
  const caveat = document.createElement('div');
  caveat.className = 'notice notice-info';
  caveat.style.marginBottom = 'var(--ca-space-4)';
  caveat.innerHTML = html`
    <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
    <div style="font-size: var(--ca-text-xs);">
      <strong style="font-weight: var(--ca-weight-medium);">What this is not.</strong>
      Every figure here is arithmetic on what you entered — there are no assumed rates and no
      country defaults. EBITDA is held flat and the loans amortise on level payments, so a
      seasonal operation or a bullet repayment will read differently. It is not a credit
      decision, an offer of terms, or a substitute for a lender's own model.
    </div>
  `;
  results.appendChild(caveat);

  const actions = document.createElement('div');
  actions.className = 'row';
  actions.style.cssText = 'gap: var(--ca-space-3); flex-wrap: wrap;';
  const again = document.createElement('button');
  again.className = 'btn btn-secondary';
  again.textContent = 'Change the stack';
  again.addEventListener('click', () => {
    page.querySelector('#capital-results').hidden = true;
    page.querySelector('#cap-form').hidden = false;
    page.querySelector('#cap-form').scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  actions.appendChild(again);
  actions.appendChild(
    PrintButton({
      targetId: 'capital-results',
      title: 'Capital Stack',
      subtitle: `${money(r.sources)} across ${funded.length} layer${funded.length === 1 ? '' : 's'}`,
    })
  );
  results.appendChild(actions);

  results.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
